import React, { memo } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { Control, Controller, FieldErrors } from 'react-hook-form';

import { StringMapDropdown } from '@/components';
import { StringMapObjectTypeCode } from '@/components/common/string-map-dropdown';
import { useFormNav } from '@/hooks/useFormNav';

import type { AspirationFormValues } from '../utils/schema';

type Props = {
  control: Control<AspirationFormValues>;
  errors: FieldErrors<AspirationFormValues>;
  leftTotal: number;
  rightTotal: number;
  scheduleOptions?: { label: string; value: string }[];
  ovarySideFirst: string;
  isBrazil: boolean;
};

type NumberInputProps = {
  field: any;
  onFocus: React.FocusEventHandler<HTMLInputElement>;
};
const NumberInput = memo(({ field, onFocus }: NumberInputProps) => (
  <input
    {...field}
    type="number"
    step={1}
    min={0}
    inputMode="numeric"
    value={field.value ?? 0}
    onChange={(e) =>
      field.onChange(e.target.value === '' ? 0 : Number(e.target.value))
    }
    onFocus={onFocus}
    className="h-7 w-full rounded border border-slate-300 px-1 text-center text-xs"
  />
));
NumberInput.displayName = 'NumberInput';

const TotalCell = memo(({ value }: { value: number }) => (
  <span className="h-7 w-full rounded border border-slate-300 bg-slate-50 px-2 text-center text-base leading-7 font-bold">
    {value}
  </span>
));
TotalCell.displayName = 'TotalCell';

const RowLabel = memo(({ children }: { children: React.ReactNode }) => (
  <label className="self-center text-xs">{children}</label>
));
RowLabel.displayName = 'RowLabel';

export const OvaryFieldsSection = ({
  control,
  errors,
  leftTotal,
  rightTotal,
  scheduleOptions = [],
  ovarySideFirst,
  isBrazil,
}: Props) => {
  const { ref, onKeyDownCapture, selectOnFocus, handleEnterKeyAdvance } =
    useFormNav<HTMLDivElement>();

  const weightScheduleLabel = ovarySideFirst?.includes('Right')
    ? 'Weight/Next Schedule'
    : 'Next Schedule/Weight';

  return (
    <div
      className="aspiration-search-form"
      ref={ref}
      onKeyDownCapture={(e) => {
        onKeyDownCapture(e);
        handleEnterKeyAdvance(e);
      }}
    >
      <div className="grid grid-cols-[8.5rem_1fr_1fr] items-center gap-x-3 gap-y-1 text-xs">
        <div />
        <div className="text-center font-semibold">Left</div>
        <div className="text-center font-semibold">Right</div>

        <RowLabel>Large:</RowLabel>
        <Controller
          name="leftLarge"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />
        <Controller
          name="rightLarge"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />
        {(errors.leftLarge || errors.rightLarge) && (
          <span className="col-span-3 -mt-1 text-[10px] text-red-600">
            {String(
              errors.leftLarge?.message || errors.rightLarge?.message || '',
            )}
          </span>
        )}

        <RowLabel>Medium:</RowLabel>
        <Controller
          name="leftMedium"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />
        <Controller
          name="rightMedium"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />

        <RowLabel>Small:</RowLabel>
        <Controller
          name="leftSmall"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />
        <Controller
          name="rightSmall"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />

        <RowLabel>Total:</RowLabel>
        <TotalCell value={leftTotal} />
        <TotalCell value={rightTotal} />

        <RowLabel>CLs:</RowLabel>
        <Controller
          name="leftCls"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />
        <Controller
          name="rightCls"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />

        <RowLabel>Cysts:</RowLabel>
        <Controller
          name="leftCysts"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />
        <Controller
          name="rightCysts"
          control={control}
          render={({ field }) => (
            <NumberInput field={field} onFocus={selectOnFocus} />
          )}
        />

        <RowLabel>{weightScheduleLabel}</RowLabel>
        <Controller
          name="nextScheduleId"
          control={control}
          render={({ field }) => (
            <Dropdown
              value={field.value}
              options={scheduleOptions}
              onChange={(e) => field.onChange(e.value)}
              className="w-full text-xs"
              pt={{
                root: { className: 'h-7' },
                input: { className: 'h-full flex items-center px-1' },
                trigger: { className: 'h-7 w-6' },
              }}
              dropdownIcon="pi pi-chevron-down text-xs"
              filter
            />
          )}
        />
        <Controller
          name="weight"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              className="h-7 w-full rounded border border-slate-300 px-1 text-xs"
              onFocus={selectOnFocus}
            />
          )}
        />

        {isBrazil && (
          <>
            <RowLabel>Donor Category/Body Condition Score:</RowLabel>
            <Controller
              name="donorCategory"
              control={control}
              render={({ field }) => (
                <StringMapDropdown
                  appendTo={undefined}
                  value={field.value}
                  objectTypeCode={StringMapObjectTypeCode.DONOR_CATEGORY}
                  attributeName="tog_donorcategory"
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            <Controller
              name="bodyConditionScore"
              control={control}
              render={({ field }) => (
                <StringMapDropdown
                  appendTo={undefined}
                  value={field.value}
                  objectTypeCode={StringMapObjectTypeCode.BODY_CONDITION_SCORE}
                  attributeName="tog_bodyconditionscore"
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
          </>
        )}
      </div>
    </div>
  );
};
